import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { t as trans } from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Stack from '@mui/material/Stack';
import shuffle from 'lodash/shuffle';
import { usePatchLoanCancel } from '@repo/api/endpoints/loan';
import questionMarkIconUrl from '@repo/assets/icons/question-mark.svg?url';
import { Button, Loader, SelectCustom } from '@repo/ui';
import { apiResponseType } from '@repo/shared/types';
import { useApiErrorStoreActions } from '@repo/api/client';
import { colorPalette } from '@repo/mui-config/color-palette';
import { useMainContext } from '@repo/app-contexts/main';

import { useMainHubStoreActions, useMainHubStoreState } from '@/store/main-hub.store';
import { useModalStore } from '@/components/modal/modal.store';

import { WhyDoYouCancelLoanSchema } from './why-do-you-cancel-loan-moda.schema';
import type { WhyDoYouCancelLoanFields } from './why-do-you-cancel-loan-moda.schema';

const defaultValues = {
  reason: '',
};

const options = shuffle([
  {
    id: 0,
    name: trans(
      'dashboard.loanList.whyDoYouCancelLoanModal.form.select.options.doNotNeedThatMoney',
    ),
    value: 'Nie potrzebuję tych środków',
  },
  {
    id: 1,
    name: trans(
      'dashboard.loanList.whyDoYouCancelLoanModal.form.select.options.justCheckingMyAbility',
    ),
    value: 'Tylko sprawdzam swoją zdolność',
  },
  {
    id: 2,
    name: trans(
      'dashboard.loanList.whyDoYouCancelLoanModal.form.select.options.conditionsAreNotFavorableForMe',
    ),
    value: 'Warunki nie są dla mnie korzystne',
  },
  {
    id: 3,
    name: trans(
      'dashboard.loanList.whyDoYouCancelLoanModal.form.select.options.preferLoanFromBank',
    ),
    value: 'Wolę pożyczkę w banku',
  },
]);

export function WhyDoYouCancelLoan() {
  const { hash, token } = useMainContext();
  const { getApiErrorCode } = useApiErrorStoreActions();
  const apiErrorCode = getApiErrorCode();
  const { t } = useTranslation();
  const methods = useForm<WhyDoYouCancelLoanFields>({
    defaultValues,
    resolver: zodResolver(WhyDoYouCancelLoanSchema),
  });
  const { additionalProps } = useModalStore();
  const { merchantResponseData } = useMainHubStoreState();
  const { setCurrentView } = useMainHubStoreActions();
  const { mutate: mutateLoanCancel, isPending } = usePatchLoanCancel({
    hash,
    token,
  });

  const additionalPropsLoanDataId =
    typeof additionalProps.loanData === 'object' &&
    additionalProps.loanData !== null &&
    'id' in additionalProps.loanData &&
    typeof additionalProps.loanData.id === 'number'
      ? additionalProps.loanData.id
      : null;

  const handleCancelLoan = (value: WhyDoYouCancelLoanFields) => {
    if (merchantResponseData) {
      const loans = merchantResponseData.data.loans;
      const currentLoanId = additionalPropsLoanDataId ?? loans[loans.length - 1].id;

      const provider = merchantResponseData.data.loans.find(
        (loan) => loan.id === currentLoanId,
      )?.provider;

      if (provider) {
        const dataToSend = {
          requestId: merchantResponseData.requestId,
          type: apiResponseType.enum.LOAN_CANCEL,
          loanId: currentLoanId,
          provider,
          reason: value.reason,
        };
        mutateLoanCancel(dataToSend, {
          onSuccess: () => {
            if (typeof additionalProps.handleOnCancelSuccess === 'function')
              additionalProps.handleOnCancelSuccess();
          },
        });
      } else {
        setCurrentView('SERVICE_NOT_AVAILABLE');
      }
    }
  };

  return (
    <Box data-testid="why-do-you-cancel-loan" marginTop={4}>
      <Typography component="p" marginBottom={3} textAlign="center" variant="h5">
        {t('dashboard.loanList.whyDoYouCancelLoanModal.title')}
      </Typography>
      <Box alignItems="center" display="flex" justifyContent="center" marginBottom={6}>
        <Box component="img" src={questionMarkIconUrl} alt="Question Mark Icon" />
      </Box>
      <Typography marginBottom={3} textAlign="center">
        {t('dashboard.loanList.whyDoYouCancelLoanModal.description')}
      </Typography>
      {isPending ? (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
          <Loader />
        </Stack>
      ) : null}
      <FormProvider {...methods}>
        <form onSubmit={(event) => void methods.handleSubmit(handleCancelLoan)(event)}>
          <Box marginBottom={4}>
            <SelectCustom
              name="reason"
              options={options}
              placeholder={t('dashboard.loanList.whyDoYouCancelLoanModal.form.select.label')}
            />
          </Box>

          <Button
            color="info"
            fullWidth
            sx={{
              paddingY: '12px',
              alignItems: 'center',
            }}
            type="submit"
            variant="contained"
          >
            {t('dashboard.loanList.whyDoYouCancelLoanModal.form.button')}
          </Button>
        </form>
      </FormProvider>
      {apiErrorCode ? (
        <Typography color={colorPalette.error.main} marginTop={1} textAlign="center">
          {t('dashboard.loanList.whyDoYouCancelLoanModal.errorOccurred')} {apiErrorCode}
        </Typography>
      ) : null}
    </Box>
  );
}
